import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { z } from 'zod';
import { isAllowedPhonePrefix, normalizeAllowedPhonePrefixes } from '@/lib/phone-prefix';

export { normalizeAllowedPhonePrefixes } from '@/lib/phone-prefix';

export type PhoneValidationMessages = {
    required: string;
    invalid: string;
    prefixNotAllowed?: string;
};

export type PhoneValidationOptions = {
    allowedPhonePrefixes?: string | string[];
};

/**
 * Normalizes a valid phone number to E.164 (e.g. "+393331234567").
 * Returns null when the input is empty or not a valid phone number.
 */
export const normalizePhoneToE164 = (phone: string): string | null => {
    const trimmed = phone.trim();
    if (!trimmed || !isValidPhoneNumber(trimmed)) {
        return null;
    }
    return parsePhoneNumber(trimmed).format('E.164');
};

/**
 * Returns null for an empty optional phone or when a forced phone-input dial code
 * is the only value (for example "+39", "+39 ", "+39()"). Other values are preserved as entered.
 */
export const normalizeOptionalPhone = (phone?: string | null, _options?: PhoneValidationOptions): string | null => {
    const trimmed = phone?.trim();
    if (!trimmed) {
        return null;
    }

    const compact = trimmed.replace(/[^\d+]/g, '');
    if (!compact || compact === '+' || /^\+\d{1,4}$/.test(compact)) {
        return null;
    }

    return trimmed;
};

export const isPhonePrefixAllowed = (phone: string, allowedPrefixes: readonly string[]): boolean =>
    isAllowedPhonePrefix(phone, [...allowedPrefixes]);

export const createRequiredPhoneSchema = (messages: PhoneValidationMessages, options?: PhoneValidationOptions) => {
    const allowedPhonePrefixes = normalizeAllowedPhonePrefixes(options?.allowedPhonePrefixes);

    return z
        .string()
        .trim()
        .min(1, messages.required)
        .refine((value) => isValidPhoneNumber(value), {
            message: messages.invalid,
        })
        .refine((value) => isPhonePrefixAllowed(value, allowedPhonePrefixes), {
            message: messages.prefixNotAllowed ?? messages.invalid,
        });
};

export const createOptionalPhoneSchema = (
    messages: Pick<PhoneValidationMessages, 'invalid'>,
    options?: PhoneValidationOptions
) => {
    const allowedPhonePrefixes = normalizeAllowedPhonePrefixes(options?.allowedPhonePrefixes);

    return z
        .string()
        .trim()
        .superRefine((value, ctx) => {
            const normalized = normalizeOptionalPhone(value, options);
            if (!normalized) {
                return;
            }

            if (!isValidPhoneNumber(normalized) || !isPhonePrefixAllowed(normalized, allowedPhonePrefixes)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: messages.invalid,
                });
            }
        })
        .transform((value) => normalizeOptionalPhone(value, options) ?? '');
};
